import BraintreeClient from './BraintreeClient';
import {
	displayPaymentInfo,
	clearPaymentInfo,
	displayPaymentError,
	displayPaymentWarning,
} from './braintreePaymentInfoSection';

const GOOGLE_PAY_BUTTON_ID = 'gpay-button-online-api-id';

export default class BraintreeGooglePayPayment extends BraintreeClient {
	constructor() {
		super();
		this.googlepayMerchantId =
			BraintreeClient.getBraintreeConfig().googlepayMerchantId;
		this.init();
		this.threeDSecure = null;
		this.paymentInProgress = false;
	}

	get environment() {
		const env = window?.od?.env;
		const environmentMode = env === 'prod' ? 'PRODUCTION' : 'TEST';
		return env ? environmentMode : undefined;
	}

	get paymentRequest() {
		return {
			apiVersion: 2,
			apiVersionMinor: 0,
			allowedPaymentMethods:
				this.googlePaymentInstance.createPaymentDataRequest()
					.allowedPaymentMethods,
			existingPaymentMethodRequired: true,
		};
	}

	async init() {
		try {
			if (!this.environment || !this.googlepayMerchantId) {
				displayPaymentError(errorTranslations.braintree.googlepay.init.error);
				return;
			}

			this.paymentsClient = new google.payments.api.PaymentsClient({
				environment: this.environment,
			});

			const clientInstance = await this.createClient();

			this.threeDSecure = await this.braintree.threeDSecure.create({
				client: clientInstance,
				authorization: this.paymentToken,
				version: 2,
			});

			this.threeDSecure.on('customer-canceled', () => {
				displayPaymentWarning(
					errorTranslations.braintree.googlepay['3ds'].canceled,
				);
				this.paymentInProgress = false;
				this.updateButtonStyle();
			});

			this.googlePaymentInstance =
				await this.createGooglePayment(clientInstance);

			const isReady = await this.paymentsClient.isReadyToPay(
				this.paymentRequest,
			);
			if (isReady.result) this.initializeUI();
		} catch (error) {
			displayPaymentError(errorTranslations.braintree.googlepay.init.error);
		}
	}

	createGooglePayment(clientInstance) {
		return new Promise((resolve, reject) => {
			this.braintree.googlePayment.create(
				{
					client: clientInstance,
					googlePayVersion: 2,
					googleMerchantId: this.googlepayMerchantId,
				},
				(error, instance) => (error ? reject(error) : resolve(instance)),
			);
		});
	}

	initializeUI() {
		const container = document.getElementById('googlepay-button');
		if (!container) return;

		this.createGooglePayButton(container);
		if (this.navigation.isAsmAgentLoggedIn) this.updateButtonStyle();
		this.setupTermsCheckboxListener();

		const infoSection = document.getElementById(
			'braintree-payment-info-section',
		);
		if (infoSection) {
			infoSection.innerHTML = '';
		}
	}

	createGooglePayButton(container) {
		const button = this.paymentsClient.createButton({
			buttonColor: 'default',
			buttonType: 'checkout',
			buttonRadius: 4,
			buttonSizeMode: 'fill',
			onClick: this.handlePaymentInitiation,
			allowedPaymentMethods: this.paymentRequest.allowedPaymentMethods,
		});

		container.appendChild(button);
		this.toggleLoadingState(false);
	}

	handlePaymentInitiation = async event => {
		event.preventDefault();

		if (!this.validateTerms()) return;

		try {
			this.paymentInProgress = true;
			this.updateButtonStyle();
			clearPaymentInfo();

			const paymentData = await this.fetchPaymentData();

			const { nonce, details } =
				await this.googlePaymentInstance.parseResponse(paymentData);

			if (details.isNetworkTokenized === false) {
				// 3DSecure payment flow
				displayPaymentInfo(
					errorTranslations.braintree.googlepay['3ds'].processing,
				);

				const billingAddress = await this.getBillingAddress();
				const threeDSecureParameters = {
					amount: this.amount,
					nonce,
					bin: details.bin,
					email: this.dataLayer.userInfo.userId,
					challengeRequested: true,
					billingAddress,
					onLookupComplete: (data, next) => {
						next();
					},
				};

				// Initiate 3DS verification.
				const threeDSecurePayload = await this.threeDSecure.verifyCard(
					threeDSecureParameters,
				);

				if (
					threeDSecurePayload.rawCardinalSDKVerificationData.Payment
						.ExtendedData.ChallengeCancel !== '01'
				) {
					// Verify that liability has shifted or can shift before proceeding.
					if (threeDSecurePayload.liabilityShifted) {
						const eciFlagHiddenInput = this.createHiddenInput(
							'eciFlag',
							threeDSecurePayload.threeDSecureInfo?.eciFlag || null,
						);
						this.form.appendChild(eciFlagHiddenInput);

						this.submitFormWithBraintreeNonce(
							this.form,
							threeDSecurePayload.nonce,
						);
					} else {
						// 3D Secure verification failed to shift liability
						displayPaymentWarning(
							errorTranslations.braintree.googlepay['3ds'].failed,
						);
						this.paymentInProgress = false;
						this.updateButtonStyle();
					}
				}
			} else {
				// DPAN, 3DSecure not needed
				displayPaymentInfo(errorTranslations.braintree.googlepay.processing);
				this.submitFormWithBraintreeNonce(this.form, nonce);
			}
		} catch (error) {
			// Handle errors during 3D Secure verification
			displayPaymentError(
				`${errorTranslations.braintree.googlepay.payment.error.replace('{0}', error.message || errorTranslations.braintree.googlepay.payment.error_unknown)}`,
			);
			this.paymentInProgress = false;
			this.updateButtonStyle();
		}
	};

	validateTerms() {
		if (this.termsAndConditionsCheckbox?.checked) return true;
		this.updateButtonStyle();
		this.form.requestSubmit();
		return false;
	}

	async fetchPaymentData() {
		const paymentRequest = this.googlePaymentInstance.createPaymentDataRequest({
			transactionInfo: {
				currencyCode: this.currency,
				totalPriceStatus: 'FINAL',
				totalPrice: this.amount,
			},
		});

		this.configureBillingAddress(paymentRequest);
		return this.paymentsClient.loadPaymentData(paymentRequest);
	}

	configureBillingAddress(paymentRequest) {
		const [paymentMethod] = paymentRequest.allowedPaymentMethods;
		paymentMethod.parameters.billingAddressRequired = true;
		paymentMethod.parameters.billingAddressParameters = {
			format: 'FULL',
			phoneNumberRequired: true,
		};
	}

	toggleLoadingState(showLoading = true) {
		const loadingBtn = document.getElementById('loading-googlepay-button');
		if (loadingBtn) loadingBtn.hidden = !showLoading;
	}

	setupTermsCheckboxListener() {
		this.termsAndConditionsCheckbox?.addEventListener(
			'change',
			this.updateButtonStyle.bind(this),
		);
	}

	updateButtonStyle() {
		const button = document.getElementById(GOOGLE_PAY_BUTTON_ID);
		if (!button) return;

		const shouldDisable =
			!this.termsAndConditionsCheckbox?.checked ||
			this.navigation.isAsmAgentLoggedIn ||
			this.paymentInProgress;

		button.style.opacity = shouldDisable ? '0.5' : '';
		button.style.cursor = shouldDisable ? 'not-allowed' : '';
		button.toggleAttribute('disabled', shouldDisable);
	}

	async getBillingAddress() {
		const response = await fetch(`/ajax/checkout/billing-address/data`);
		const data = await response.json();
		const billing = data.billingAddress;
		return {
			givenName: billing?.contactName,
			streetAddress: billing?.line1,
			extendedAddress: billing?.line2,
			locality: billing?.town,
			postalCode: billing?.postalCode,
			countryCodeAlpha2: billing?.countryIso,
			phoneNumber: billing?.phone,
		};
	}
}
